import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "@/components/routes/AppRoutes";
import { GlobalProfileCheck } from "@/components/global/GlobalProfileCheck";
import { ThemeProvider } from "@/components/theme/ThemeProvider";
import { UserThemeApplier } from "@/components/theme/UserThemeApplier";
import { AuthProvider } from "@/contexts/AuthContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // ✅ ENHANCED RETRY LOGIC for 500 errors
        const errorMessage = error?.message || '';
        
        // Don't retry timeout errors or auth errors
        if (errorMessage.includes('timeout') || 
            errorMessage.includes('SUPABASE_TIMEOUT') ||
            errorMessage.includes('not authenticated')) {
          return false;
        }
        
        // Retry network errors up to 2 times
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
      refetchOnWindowFocus: false,
      // ✅ INCREASED stale time to reduce query frequency
      staleTime: 30000, // 30 seconds instead of 10
      // ✅ INCREASED cache time to reduce pressure
      gcTime: 600000, // 10 minutes instead of 5
      // ✅ PREVENT CACHE CORRUPTION on errors
      meta: {
        errorPolicy: 'none'
      }
    },
    mutations: {
      retry: (failureCount, error) => {
        // ✅ SIMILAR LOGIC for mutations
        const errorMessage = error?.message || '';
        
        if (errorMessage.includes('timeout') || 
            errorMessage.includes('SUPABASE_TIMEOUT')) {
          return false;
        }
        
        return failureCount < 1; // Only retry mutations once
      },
      retryDelay: 2000 // 2 second delay for mutation retries
    }
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <ThemeProvider>
          <Toaster />
          <BrowserRouter>
            <UserThemeApplier />
            <AppRoutes />
            <GlobalProfileCheck />
          </BrowserRouter>
        </ThemeProvider>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
